/**
 * Whiskery Sour® - Product Page Cleanup & FAQ Handler
 * Minimog Theme
 *
 * Features:
 * - Remove empty elements and duplicates on DOM load
 * - Initialize FAQ accordion with proper accessibility
 * - Handle Q&A form submission via AJAX
 * - Track user interactions with Google Analytics/dataLayer
 * - Comprehensive error handling and validation
 */

(function() {
  'use strict';

  // Configuration object
  const CONFIG = {
    selectors: {
      pickupAvailability: 'pickup-availability',
      productForms: '.product-form, form[id*="product"]',
      priceBlocks: '[data-product-price], .product-price',
      productTitles: '.product-title, .product__title, h1[data-product-title]',
      addToCartButtons: 'button[name="add"], [data-add-to-cart]',
      faqContainer: '[data-faq-container]',
      faqTriggers: '[data-faq-trigger]',
      faqForm: '[data-faq-form]',
      formInputs: '[data-faq-input]',
      submitButton: '[data-submit-btn]',
      successMessage: '#form-success-message'
    },
    classes: {
      error: 'error',
      loading: 'loading',
      show: 'show'
    },
    messages: {
      nameRequired: 'Please enter your name',
      emailRequired: 'Please enter a valid email address',
      questionRequired: 'Please ask your question',
      submitError: 'There was an error submitting your question. Please try again.',
      networkError: 'Network error. Please check your connection and try again.'
    },
    contactEndpoint: '/contact'
  };

  /**
   * DOM Ready Initialization
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', onDOMReady);
    } else {
      onDOMReady();
    }
  }

  /**
   * Main initialization function
   */
  function onDOMReady() {
    removeEmptyPickupElements();
    removeEmptySubscriptionWidgets();
    removeDuplicateForms();
    removeDuplicatePriceBlocks();
    removeDuplicateTitles();
    removeDuplicateAddToCartButtons();
    initializeFAQAccordion();
    initializeFAQForm();

    // Log initialization completion
    if (window.console && window.console.debug) {
      console.debug('[Whiskery Sour] Product page cleanup and FAQ initialization complete');
    }
  }

  /**
   * CLEANUP FUNCTIONS
   */

  function removeEmptyPickupElements() {
    const pickupElements = document.querySelectorAll(CONFIG.selectors.pickupAvailability);

    pickupElements.forEach(function(element, index) {
      const isEmpty = element.children.length === 0;
      const hasNoContent = !element.textContent.trim();

      if (isEmpty || hasNoContent) {
        try {
          element.remove();
          logCleanup('pickup-availability', 'removed empty element');
        } catch (e) {
          logError('Failed to remove pickup element', e);
        }
      }
    });
  }

  function removeEmptySubscriptionWidgets() {
    const subscriptionSelectors = [
      '.product-form__subscription',
      '.selling-plan-group',
      '[data-subscription-form]'
    ];

    subscriptionSelectors.forEach(function(selector) {
      const elements = document.querySelectorAll(selector);
      elements.forEach(function(element) {
        try {
          element.remove();
          logCleanup('subscription-widget', 'removed for non-subscription product');
        } catch (e) {
          logError('Failed to remove subscription widget', e);
        }
      });
    });
  }

  function removeDuplicateForms() {
    const allForms = document.querySelectorAll(CONFIG.selectors.productForms);
    let formCount = 0;

    allForms.forEach(function(form) {
      formCount++;
      if (formCount > 1 && form.style.display !== 'none') {
        try {
          form.style.display = 'none';
          form.setAttribute('aria-hidden', 'true');
          logCleanup('product-form', 'hid duplicate form #' + formCount);
        } catch (e) {
          logError('Failed to hide duplicate form', e);
        }
      }
    });

    if (formCount > 1) {
      console.warn('[Whiskery Sour] Found ' + formCount + ' product forms. Displaying first only.');
    }
  }

  function removeDuplicatePriceBlocks() {
    const priceBlocks = document.querySelectorAll(CONFIG.selectors.priceBlocks);
    let priceCount = 0;

    priceBlocks.forEach(function(block) {
      if (isInsideHiddenElement(block)) return;

      priceCount++;
      if (priceCount > 1 && block.style.display !== 'none') {
        try {
          block.style.display = 'none';
          logCleanup('price-block', 'hid duplicate price block #' + priceCount);
        } catch (e) {
          logError('Failed to hide duplicate price block', e);
        }
      }
    });

    if (priceCount > 1) {
      console.warn('[Whiskery Sour] Found ' + priceCount + ' price blocks. Displaying first only.');
    }
  }

  function removeDuplicateTitles() {
    const titles = document.querySelectorAll(CONFIG.selectors.productTitles);
    let titleCount = 0;

    titles.forEach(function(title) {
      if (isInsideHiddenElement(title)) return;

      titleCount++;
      if (titleCount > 1 && title.style.display !== 'none') {
        try {
          title.style.display = 'none';
          logCleanup('product-title', 'hid duplicate title #' + titleCount);
        } catch (e) {
          logError('Failed to hide duplicate title', e);
        }
      }
    });

    if (titleCount > 1) {
      console.warn('[Whiskery Sour] Found ' + titleCount + ' product titles. Displaying first only.');
    }
  }

  function removeDuplicateAddToCartButtons() {
    const buttons = document.querySelectorAll(CONFIG.selectors.addToCartButtons);
    let buttonCount = 0;

    buttons.forEach(function(button) {
      if (isInsideHiddenElement(button)) return;

      buttonCount++;
      if (buttonCount > 1 && button.style.display !== 'none') {
        try {
          button.style.display = 'none';
          logCleanup('add-to-cart', 'hid duplicate button #' + buttonCount);
        } catch (e) {
          logError('Failed to hide duplicate button', e);
        }
      }
    });

    if (buttonCount > 1) {
      console.warn('[Whiskery Sour] Found ' + buttonCount + ' add-to-cart buttons. Displaying first only.');
    }
  }

  function isInsideHiddenElement(element) {
    let parent = element.parentElement;
    while (parent) {
      if (parent.style.display === 'none' || parent.getAttribute('aria-hidden') === 'true') {
        return true;
      }
      parent = parent.parentElement;
    }
    return false;
  }

  /**
   * FAQ ACCORDION FUNCTIONS
   */

  function initializeFAQAccordion() {
    const triggers = document.querySelectorAll(CONFIG.selectors.faqTriggers);

    if (triggers.length === 0) return;

    triggers.forEach(function(trigger, index) {
      trigger.addEventListener('click', function() {
        toggleFAQItem(this);
      });

      trigger.addEventListener('keydown', function(e) {
        handleFAQKeydown(e, triggers, index);
      });
    });

    logCleanup('faq-accordion', 'initialized with ' + triggers.length + ' items');
  }

  function toggleFAQItem(trigger) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    const contentId = trigger.getAttribute('aria-controls');
    const content = document.getElementById(contentId);

    if (!content) return;

    const otherTriggers = document.querySelectorAll(CONFIG.selectors.faqTriggers);
    otherTriggers.forEach(function(otherTrigger) {
      if (otherTrigger !== trigger && otherTrigger.getAttribute('aria-expanded') === 'true') {
        closeFAQItem(otherTrigger);
      }
    });

    if (isExpanded) {
      closeFAQItem(trigger);
    } else {
      openFAQItem(trigger);
    }
  }

  function openFAQItem(trigger) {
    trigger.setAttribute('aria-expanded', 'true');
    const contentId = trigger.getAttribute('aria-controls');
    const content = document.getElementById(contentId);

    if (content) {
      content.style.maxHeight = content.scrollHeight + 'px';
      fireDataLayerEvent('faq_opened', {
        faq_item: trigger.id,
        faq_question: trigger.querySelector('span').textContent
      });
    }
  }

  function closeFAQItem(trigger) {
    trigger.setAttribute('aria-expanded', 'false');
    const contentId = trigger.getAttribute('aria-controls');
    const content = document.getElementById(contentId);

    if (content) {
      content.style.maxHeight = '0';
      fireDataLayerEvent('faq_closed', {
        faq_item: trigger.id
      });
    }
  }

  function handleFAQKeydown(e, triggers, currentIndex) {
    let targetIndex = null;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        targetIndex = (currentIndex + 1) % triggers.length;
        triggers[targetIndex].focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        targetIndex = (currentIndex - 1 + triggers.length) % triggers.length;
        triggers[targetIndex].focus();
        break;
      case 'Home':
        e.preventDefault();
        triggers[0].focus();
        break;
      case 'End':
        e.preventDefault();
        triggers[triggers.length - 1].focus();
        break;
    }
  }

  /**
   * FAQ FORM FUNCTIONS
   */

  function initializeFAQForm() {
    const form = document.querySelector(CONFIG.selectors.faqForm);

    if (!form) {
      // No FAQ form on this page — nothing to initialise. (Normal on most pages.)
      return;
    }

    const inputs = form.querySelectorAll(CONFIG.selectors.formInputs);
    inputs.forEach(function(input) {
      input.addEventListener('focus', function() {
        const group = this.closest('[data-form-group]');
        if (group) {
          group.classList.remove(CONFIG.classes.error);
          const errorMessage = group.querySelector('[data-error-message]');
          if (errorMessage) {
            errorMessage.textContent = '';
          }
        }
      });
    });

    form.addEventListener('submit', function(e) {
      e.preventDefault();
      submitFAQForm(this);
    });

    logCleanup('faq-form', 'initialized with validation');
  }

  function validateFAQForm(form) {
    const errors = {};
    const nameInput = form.querySelector('input[name="name"]');
    const emailInput = form.querySelector('input[name="email"]');
    const messageInput = form.querySelector('textarea[name="message"]');

    if (!nameInput.value.trim()) {
      errors.name = CONFIG.messages.nameRequired;
    }

    if (!emailInput.value.trim() || !isValidEmail(emailInput.value)) {
      errors.email = CONFIG.messages.emailRequired;
    }

    if (!messageInput.value.trim() || messageInput.value.trim().length < 5) {
      errors.message = CONFIG.messages.questionRequired;
    }

    return errors;
  }

  function isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  function displayFAQFormErrors(form, errors) {
    form.querySelectorAll('[data-form-group]').forEach(function(group) {
      group.classList.remove(CONFIG.classes.error);
      const errorMessage = group.querySelector('[data-error-message]');
      if (errorMessage) {
        errorMessage.textContent = '';
      }
    });

    Object.keys(errors).forEach(function(fieldName) {
      const group = form.querySelector('[data-form-group="' + fieldName + '"]');
      if (group) {
        group.classList.add(CONFIG.classes.error);
        const errorMessage = group.querySelector('[data-error-message="' + fieldName + '"]');
        if (errorMessage) {
          errorMessage.textContent = errors[fieldName];
        }
      }
    });

    const firstErrorGroup = form.querySelector('[data-form-group].' + CONFIG.classes.error);
    if (firstErrorGroup) {
      firstErrorGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function submitFAQForm(form) {
    const submitButton = form.querySelector(CONFIG.selectors.submitButton);
    const successMessage = document.querySelector(CONFIG.selectors.successMessage);

    const errors = validateFAQForm(form);
    if (Object.keys(errors).length > 0) {
      displayFAQFormErrors(form, errors);
      return;
    }

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add(CONFIG.classes.loading);
      submitButton.textContent = 'Sending...';
    }

    const formData = new FormData(form);
    const data = {
      form_type: formData.get('form_type'),
      contact: {
        first_name: formData.get('name'),
        email: formData.get('email'),
        body: formData.get('message') + '\n\n---\nProduct: ' + formData.get('product_name') + '\nProduct URL: ' + formData.get('product_url')
      }
    };

    fetch(CONFIG.contactEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(data)
    })
    .then(function(response) {
      if (!response.ok) {
        throw new Error('Form submission failed: ' + response.statusText);
      }
      return response.json();
    })
    .then(function(data) {
      form.reset();

      if (successMessage) {
        successMessage.classList.add(CONFIG.classes.show);
        setTimeout(function() {
          successMessage.classList.remove(CONFIG.classes.show);
        }, 5000);
      }

      fireDataLayerEvent('product_question_asked', {
        product_id: formData.get('product_id'),
        product_name: formData.get('product_name'),
        customer_email: formData.get('email')
      });

      logCleanup('faq-form', 'submission successful');
    })
    .catch(function(error) {
      logError('FAQ form submission failed', error);

      if (successMessage) {
        successMessage.textContent = CONFIG.messages.submitError;
        successMessage.classList.add(CONFIG.classes.show);
        setTimeout(function() {
          successMessage.classList.remove(CONFIG.classes.show);
        }, 5000);
      }
    })
    .finally(function() {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.classList.remove(CONFIG.classes.loading);
        submitButton.textContent = 'Send Question';
      }
    });
  }

  /**
   * GOOGLE ANALYTICS / DATA LAYER
   */

  function fireDataLayerEvent(eventName, eventData) {
    if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      window.dataLayer.push({
        event: eventName,
        event_category: 'product_interaction',
        timestamp: new Date().toISOString(),
        ...eventData
      });

      if (window.console && window.console.debug) {
        console.debug('[Whiskery Sour] Data Layer Event:', eventName, eventData);
      }
    }
  }

  /**
   * LOGGING UTILITIES
   */

  function logCleanup(component, action) {
    if (window.console && window.console.debug) {
      console.debug('[Whiskery Sour] ' + component + ': ' + action);
    }
  }

  function logError(message, error) {
    if (window.console && window.console.error) {
      console.error('[Whiskery Sour] ' + message, error);
    }
  }

  init();

  window.WhiskerySourProductCleanup = {
    init: init,
    validateFAQForm: validateFAQForm,
    submitFAQForm: submitFAQForm,
    fireDataLayerEvent: fireDataLayerEvent
  };

})();
